<template>
  <div class="basic-info">
    <div class="box">
      <p class="id"><span>ID</span>{{ contentInfo.code }}</p>
      <div class="user-info">
        <div class="user-info-box">
          <span class="user">作成者：{{ contentInfo.createdUserName }}</span>
          <span>作成日時：{{ contentInfo.createdOn }}</span>
        </div>
        <div class="user-info-box">
          <span class="user">更新者：{{ contentInfo.savedUserName }}</span>
          <span>更新日時：{{ contentInfo.savedOn }}</span>
        </div>
      </div>
    </div>
    <h2>基本設定</h2>
    <div class="box-gray">
      <div class="name-box">
        <span class="title">件名</span>
        <input
          v-if="contentInfo.kind === 'origin'"
          type="text"
          :class="{ 'error-border': validations['name'] }"
          :value="contentInfo.name"
          @keypress.enter="$event.target.blur"
          @keyup="mergeContentInfo('name', $event.target.value)"
          @blur="saveContentInfo('name', $event.target.value)"
          key="name"
        />
        <span class="grayout" v-else key="name">{{ contentInfo.name }}</span>
        <span v-if="validations['name']" class="error-message">{{ validationMessages.name }}</span>
      </div>
      <div class="preheader-box">
        <span class="title">プリヘッダー</span>
        <input
          v-if="contentInfo.kind === 'origin'"
          type="text"
          :class="{ 'error-border': validations['preheader'] }"
          :value="contentInfo.preheader"
          @keypress.enter="$event.target.blur"
          @keyup="mergeContentInfo('preheader', $event.target.value)"
          @blur="saveContentInfo('preheader', $event.target.value)"
          key="preheader"
        />
        <span class="grayout" v-else key="preheader">{{ contentInfo.preheader }}</span>
        <span v-if="validations['preheader']" class="error-message">{{ validationMessages.preheader }}</span>
      </div>
      <div class="group">
        <span class="group-label">グループ</span>
        <ul class="batch">
          <li
            class="batch-folder normal-btn"
            @click="folders.batchOption = !folders.batchOption"
            v-on-clickaway="batchFolderHidden"
          >
            <span class="batch-input">{{ selectedFolderName }}</span>
            <transition>
              <ul class="drop-down-list drop-down-list-scrollable" v-show="folders.batchOption">
                <li> ----- </li>
                <li
                  v-for="(folder, index) in folders.data"
                  :key="index"
                  @click="addFolder(folder)"
                >
                 {{ folder.name }}
                </li>
              </ul>
            </transition>
          </li>
        </ul>
      </div>
      <div class="tag">
        <label class="title">タグ</label>
        <vue-tags-input
          v-model="tags.input"
          :tags="tags.data"
          :autocomplete-items="tags.autocompletes"
          placeholder="タグを追加してください"
          @tags-changed="onTagsChanged"
          @before-adding-tag="beforeAddingTag"
          :disabled="isLoading"
        />
      </div>
      <div class="parameter">
        <label class="title">パラメーター</label>
        <input
          v-if="contentInfo.kind === 'origin'"
          type="text"
          :class="{ 'error-border': validations['linkParams'] }"
          :value="contentInfo.linkParams"
          @keypress.enter="$event.target.blur"
          @keyup="mergeContentInfo('linkParams', $event.target.value)"
          @blur="saveContentInfo('linkParams', $event.target.value)"
          key="linkParams"
        />
        <span class="grayout" v-else key="linkParams">{{ contentInfo.linkParams }}</span>
        <span v-if="validations['linkParams']" class="error-message">{{ validationMessages.linkParams }}</span>
      </div>
    </div>
    <h2>その他</h2>
    <div class="box-gray">
      <div class="memo">
        <label class="title">メモ</label>
        <textarea
          :class="{ 'error-border': validations['memo'] }"
          :value="contentInfo.memo"
          @keyup="mergeContentInfo('memo', $event.target.value)"
          @blur="saveContentInfo('memo', $event.target.value)"
        />
        <span v-if="validations['memo']" class="error-message">{{ validationMessages.memo }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as onClickAway } from 'vue-clickaway'
import VueTagsInput from '@johmun/vue-tags-input'
import constants from '@/constants'
import jsBeautify from 'js-beautify'

const MAX_TAG_LENGTH = 25

export default {
  mixins: [
    onClickAway
  ],
  components: {
    VueTagsInput
  },
  props: [
    'enableOwnSave'
  ],
  data () {
    return {
      isLoading: false,
      tags: {
        data: [],
        input: '',
        autocompletes: [],
      },
      folders: {
        data: [],
        batchOption: false,
      },
      // 変更の際はサーバー側も対応する
      validationMessages: {
        name: '全角128文字(半角256文字)以下で入力してください',
        preheader: '100文字以下で入力してください',
        linkParams: '2000文字以下で入力してください',
        memo: '2000文字以下で入力してください',
      }
    }
  },
  computed: {
    contentInfo () {
      return this.$store.getters.getContentInfo
    },
    selectedFolderName () {
      const selectedFolder = this.folders.data.find(folder => folder.id === this.contentInfo.folderId)
      return selectedFolder ? selectedFolder.name : '未設定'
    },
    validations () {
      const validations = this.$store.getters.getContentInfoValidations
      if (!validations || Object.keys(validations).length === 0) return {}
      if (validations.link_params) {
        // バリデーションでは カラム名を返すので、スネークケースをキャメルケースに変換する必要がある
        validations['linkParams'] = validations['link_params']
        delete validations['link_params']
      }
      return validations
    }
  },
  async created () {
    this.getFolders()
    this.getTags()
  },
  methods: {
    mergeContentInfo (updateKey, value) {
      if (!this.enableOwnSave) {
        // blurがページ遷移時に発火した時は処理しない
        if (!this.$route.params.id) return
        // storeに反映
        this.$store.commit('mergeContentInfo', { [updateKey]: value })
      }
    },
    async saveContentInfo (updateKey, value) {
      // blurがページ遷移時に発火した時は処理しない
      if (!this.$route.params.id) return
      // storeに反映
      this.$store.commit('mergeContentInfo', { [updateKey]: value })
      // save
      if (this.enableOwnSave) {
        // フォームバリデーションメッセージのリセット
        this.$store.commit('updateContentInfoValidations', {})
        if (process.env.VUE_APP_ENV === 'localdev' && [99999].includes(this.$route.params.id)) {
          // 特定のURLはリクエストを送らないように
          return
        }
        const formData = new FormData()
        if (['name', 'preheader', 'linkParams', 'memo'].includes(updateKey)) {
          value = this.$utils.string.b64Encode(value)
        }
        formData.append(updateKey, value)
        const formValidationKeys = ['name', 'preheader', 'link_params', 'memo']
        formValidationKeys.forEach(val => {
          formData.append('formValidationKeys[]', val)
        })
        const json = await this.$http({
          url: `/content/save_info/${this.$route.params.id}`,
          type: 'POST',
          params: formData,
        })
        if (json && json.status === 200) {
          this.$store.commit('showMessageToast', {
            kind: 'success',
            message: '保存しました',
          })
        }
        if (json && json.validations) {
          this.$store.commit('updateContentInfoValidations', json.validations)
        }
      }
    },
    batchFolderHidden () {
      this.folders.batchOption = false
    },
    async onTagsChanged (newTags) {
      // save
      if (this.enableOwnSave) {
        this.isLoading = true
      }
      this.$store.commit('updateContentTags', newTags)
      this.tags.data = this.$store.state.contentTags
      if (this.enableOwnSave) {
        this.tags.autocompletes = []
        const formData = new FormData()
        this.tags.data.forEach(tag => {
          formData.append('tags[]', JSON.stringify({
            'tagId': tag.tagId,
            'contentTagMapId': tag.contentTagMapId,
            'name': this.$utils.string.b64Encode(tag.text)
          }))
        })
        const json = await this.$http({
          url: `/tag/save/${this.$route.params.id}`,
          type: 'POST',
          params: formData,
        })
        // 成功・失敗に関わらずタグを再取得する
        // DBで保存され、tagId や tagMapId が保存されているタグを取得するため
        this.getTags()

        this.isLoading = false
        if (this.enableOwnSave && json && json.status === 200) {
          this.$store.commit('showMessageToast', {
            kind: 'success',
            message: '保存しました',
          })
        }
      }
    },
    beforeAddingTag (newTag) {
      if (this.tags.data.length >= constants.maxTagsCount) {
        // @blur によってエラーが二回出る対策で入力文字列を削除している
        newTag.tag.text = ''
        this.tags.input = ''
        this.$store.commit(
          'showErrorMessagePopup',
          ['タグは最大' + constants.maxTagsCount + '個までです']
        )
      } else if ([...newTag.tag.text].length > MAX_TAG_LENGTH) {
        newTag.tag.text = ''
        this.tags.input = ''
        this.$store.commit(
          'showErrorMessagePopup',
          ['タグは最大' + MAX_TAG_LENGTH + '字までです']
        )
      } else {
        // 前後の半角スペース・tabを取り除いた状態にする
        newTag.tag.text = newTag.tag.text.replace(/^[ \t]+|[ \t]+$/g, '')
        this.tags.input = newTag.tag.text.replace(/^[ \t]+|[ \t]+$/g, '')
        newTag.addTag()
      }
    },
    async getFolders () {
      const json = await this.$http({
        url: `/folder/content?sortTarget=manual&sortType=asc`,
        type: 'GET',
      })
      this.folders.data = [
        {
          id: 'unset',
          name: '未設定',
        },
        ...json.data.folders.map(folder => {
          return {
            ...folder,
            name: this.$utils.string.b64Decode(folder.name),
          }
        })
      ]
    },
    async addFolder (folder) {
      this.$store.commit('mergeContentInfo', { folderId: folder.id })
      // save
      if (this.enableOwnSave) {
        const formData = new FormData()
        formData.append('targetIds[]', this.$route.params.id)
        formData.append('folder', folder.id)
        const json = await this.$http({
          url: '/folder/add/content',
          type: 'POST',
          params: formData
        })
        if (json && json.status === 200) {
          this.$store.commit('showMessageToast', {
            kind: 'success',
            message: '保存しました',
          })
        }
      }
    },
    async getTags () {
      const state = this.$store.state
      const isEditUpdated = this.$utils.string.hasEditDiff(state)
      const json = await this.$http({
        url: `/tag/${this.$route.params.id}`,
        type: 'GET',
      })
      if (json && json.status === 200) {
        this.tags.data = json.data.tags.map(tag => {
          return {
            'tagId': tag.tagId,
            'contentTagMapId': tag.contentTagMapId,
            'text': this.$utils.string.b64Decode(tag.text)
          }
        })
        // 差分更新
        this.$store.commit('updateEditDiff', {
          isUpdated: isEditUpdated,
          storeHashBeforeEdit: this.$utils.string.hashCode(
            JSON.stringify({
              contentInfo: state.contentInfo,
              sections: state.sections,
              styles: state.styles,
              mobileStyles: state.mobileStyles,
              originalStyle: state.originalStyle ? jsBeautify.css(state.originalStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
              originalHeadStyle: state.originalHeadStyle ? jsBeautify.css(state.originalHeadStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
              contentTags: this.tags.data,
            })
          )
        })
        this.$store.commit('updateContentTags', this.tags.data)
      }
    },
  },
  watch: {
    'tags.input' () {
      if (this.tags.input.length < 1) return
      const formData = new FormData()
      formData.append('input', this.tags.input)
      this.$http({
        url: '/tag/autocomplete',
        type: 'POST',
        params: formData
      }).then(json => {
        this.tags.autocompletes = json.data.tags
      })
    },
    validations (value, oldValue) {
      if (!value || Object.keys(value).length === 0 || JSON.stringify(value) === JSON.stringify(oldValue)) return
      if (this.enableOwnSave) return
      const { name, preheader, linkParams, memo } = value
      this.$store.commit('showErrorMessagePopup',
        [ name, preheader, linkParams, memo ].filter(Boolean),
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.basic-info {
  color: $commonFontColor;
  border-top: 1px solid #dbdbdb;
  line-height: 2;
  overflow-y: auto;
  height: calc(100vh - 150px);
  .grayout {
    display: block;
    font-weight: normal;
    font-size: 14px;
    color: $commonFontColor;
    width: 100%;
    padding: 5px;
    word-break: break-word;
  }
  .name-box, .preheader-box, .group, .tag, .parameter, .host-url {
    margin-bottom: 10px;
  }
  .group {
    .normal-btn {
      padding: 0;
      line-height: 1;
    }
  }
  .id {
    font-size: 14px;
    color: rgba(#484848, 0.6);
    height: 28px;
    span {
      font-size: 12px;
      display: inline-block;
      text-align: center;
      border-radius: 12px;
      background: #e4e4e4;
      width: 30px;
      height: 19px;
      margin-right: 8px;
    }
  }
  .user-info {
    padding-bottom: 20px;
    font-size: 14px;
    font-weight: normal;
    color: #6a6a6a;
    .user-info-box {
      span {
        display: inline-block;
        vertical-align: middle;
        &.user {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: calc(100% - 195px);
          max-width: 400px;
          padding-right: 10px;
        }
      }
    }
  }
  .title {
    font-size: 14px;
    font-weight: normal;
    color: #6a6a6a;
    display: block;
  }
  .name-box {
    font-size: 16px;
    font-weight: bold;
    display: block;
    > div {
      overflow: hidden;
      display: inline;
    }
  }
  .group {
    .group-label {
      font-size: 14px;
      font-weight: normal;
      color: #6a6a6a;
    }
    .batch {
      display:flex;
      align-items: center;
      flex: 1;
      li {
        span {
          display: inline-block;
          font-size: 14px;
          &::before, &::after {
            font-family: "Font Awesome 5 Free";
            font-weight: 900;
          }
          &::before {
            padding-right: 5px;
          }
        }
        &.batch-folder {
          width: 100%;
          .batch-input {
            text-align: left;
            &::before {
              content: '\f5fd';
            }
          }
          .drop-down-list {
            padding: 15px 15px 15px 10px;
          }
        }
      }
      // ドロップダウンリストクリック時のアニメーション
      .v-enter-active, .v-leave-active {
        transition: all .1s;
      }
      .v-enter,.v-leave-to {
        height: 0;
        opacity: 0;
      }
      .v-enter-to, .v-leave {
        height: 152px;
        opacity: 1;
      }
    }
  }
  .vue-tags-input {
    max-width: none;
    width: 100%;
  }
  input, .batch-input {
    color: $commonFontColor;
    background: #FFF;
    border-radius: 2px;
    line-height: 1.5;
    width: 100%;
    height: 35px;
    line-height: 35px;
    padding-left: 10px;
    box-sizing: border-box;
    border: 1px solid #ccc;
  }
  .memo {
    textarea {
      border: 1px solid #ccc;
      color: $commonFontColor;
      background: #FFF;
      border-radius: 2px;
      min-height: 180px;
      width: 100%;
      padding: 5px 10px;
      line-height: 1.5;
      box-sizing: border-box;
      &::-webkit-resizer {
        display: none;
      }
    }
  }
  h2 {
    color: rgba($baseFontColor, 0.9);
    font-size: 16px;
    font-weight: normal;
    margin: 0;
    height: 40px;
    line-height: 40px;
    padding: 0 20px 0;
    border-bottom: 0.5px solid #dbdbdb;
  }
  .box {
    padding: 10px 20px 0;
    line-height: 1.5;
    &-gray {
      padding: 10px 20px;
      background: #E4E4E4;
    }
  }
}
</style>
